import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import Feedback from "../components/Feedback";
import { CheckIcon } from "../components/Icons";
import ItemCard from "../components/ItemCard";
import Picture from "../components/Picture";
import ui from "../components/ui.module.css";
import { arePairsRight } from "../data/activityAdapter";
import { matchingLayout } from "../data/matchingLayout";

const CELEBRATE_MS = 800;
const LEFT = "left";
const RIGHT = "right";

const nameOf = (item, index) => item.label || item.asset?.alt || `Picture ${index + 1}`;

// The pairs saved for this step, as far as they still fit it. Anything that is
// not a pair of this step's pictures, or that reuses a picture already paired,
// is left out, so a damaged or out-of-date saved answer starts those pictures
// unpaired rather than breaking the step.
const savedPairsOf = (step, saved) => {
  if (!Array.isArray(saved?.pairs)) return [];
  const lefts = new Set(step.left.map((i) => i.value));
  const rights = new Set(step.right.map((i) => i.value));
  return saved.pairs.reduce((kept, p) => {
    if (
      p &&
      lefts.has(p.left) &&
      rights.has(p.right) &&
      !kept.some((k) => k.left === p.left || k.right === p.right)
    ) {
      kept.push({ left: p.left, right: p.right });
    }
    return kept;
  }, []);
};

// Tap a picture on either side, then its partner on the other side. Each pair
// gets the same number on both pictures and a line between them, so a pair
// never depends on colour alone. Tapping a paired picture picks it up again to
// change its partner. Only the finished, accepted set of pairs is saved.
//
// The two sides are shown in an order that belongs to this attempt, not in the
// order they were written, and stays the same until the attempt ends. Pairs
// already saved for the step come back when the attempt resumes on it.
const MatchingStep = ({ step, attemptId, saved, onDone, saving }) => {
  const layout = useMemo(() => matchingLayout(step, attemptId), [step, attemptId]);
  // [{ left, right }] in the order made
  const [pairs, setPairs] = useState(() => savedPairsOf(step, saved));
  const [picked, setPicked] = useState(null); // { side, value }
  const [result, setResult] = useState(null); // null | "right" | "wrong"
  const [lines, setLines] = useState([]);
  const board = useRef(null);
  const cards = useRef({});
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const pairOf = (side, value) => pairs.findIndex((p) => p[side] === value);

  const tap = (side, value) => {
    if (saving || result === "right") return;
    setResult(null);
    if (!picked || picked.side === side) {
      setPicked(picked?.side === side && picked.value === value ? null : { side, value });
      return;
    }
    const pair =
      side === LEFT
        ? { left: value, right: picked.value }
        : { left: picked.value, right: value };
    // A picture belongs to one pair at a time.
    setPairs((current) => [
      ...current.filter((p) => p.left !== pair.left && p.right !== pair.right),
      pair,
    ]);
    setPicked(null);
  };

  const complete = pairs.length === step.left.length;

  const check = () => {
    if (!complete || saving || result === "right") return;
    if (arePairsRight(step, pairs)) {
      setResult("right");
      // Saved in the order of the left-hand pictures; the order means nothing.
      const ordered = step.left.map(({ value }) => pairs.find((p) => p.left === value));
      timer.current = setTimeout(() => onDone({ pairs: ordered }), CELEBRATE_MS);
    } else {
      setResult("wrong");
    }
  };

  // The lines are drawn between the edges of the two cards of each pair and
  // redrawn whenever the pairs or the screen size change.
  const measure = useCallback(() => {
    const box = board.current?.getBoundingClientRect();
    if (!box) return;
    setLines(
      pairs
        .map((pair, i) => {
          const a = cards.current[`${LEFT}:${pair.left}`]?.getBoundingClientRect();
          const b = cards.current[`${RIGHT}:${pair.right}`]?.getBoundingClientRect();
          if (!a || !b) return null;
          const key = `${pair.left}-${pair.right}`;
          // Side by side on a narrow screen, one row above the other on a
          // wide one: join the facing edges either way.
          if (b.top >= a.bottom) {
            return {
              key,
              number: i + 1,
              x1: a.left + a.width / 2 - box.left,
              y1: a.bottom - box.top,
              x2: b.left + b.width / 2 - box.left,
              y2: b.top - box.top,
            };
          }
          return {
            key,
            number: i + 1,
            x1: a.right - box.left,
            y1: a.top + a.height / 2 - box.top,
            x2: b.left - box.left,
            y2: b.top + b.height / 2 - box.top,
          };
        })
        .filter(Boolean)
    );
  }, [pairs]);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined" || !board.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(board.current);
    return () => observer.disconnect();
  }, [measure]);

  const column = (side, items) => (
    <ul className={ui.matchColumn} aria-label={side === LEFT ? "First items" : "Partners"}>
      {items.map((item, index) => {
        const pair = pairOf(side, item.value);
        const isPicked = picked?.side === side && picked.value === item.value;
        const name = nameOf(item, index);
        return (
          <li key={item.value}>
            <ItemCard
              ref={(node) => {
                cards.current[`${side}:${item.value}`] = node;
              }}
              item={item}
              className={[
                ui.choice,
                ui.matchCard,
                isPicked && ui.picked,
                pair >= 0 && ui.paired,
                result === "right" && pair >= 0 && ui.choiceRight,
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => tap(side, item.value)}
              aria-pressed={isPicked}
              aria-label={pair >= 0 ? `${name}, pair ${pair + 1}` : name}
              disabled={saving}
            >
              {pair >= 0 && (
                <span className={`${ui.slotNumber} ${ui[`pair${pair % 6}`]}`} aria-hidden="true">
                  {pair + 1}
                </span>
              )}
              <Picture item={item} />
            </ItemCard>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <Prompt step={step} />
      <div className={`${ui.matchBoard} ${result === "wrong" ? ui.shake : ""}`} ref={board}>
        {column(LEFT, layout.left)}
        <svg className={ui.matchLines} aria-hidden="true" focusable="false">
          {lines.map((line) => (
            <line
              key={line.key}
              className={ui[`pairLine${(line.number - 1) % 6}`]}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
            />
          ))}
        </svg>
        {column(RIGHT, layout.right)}
      </div>
      <button
        type="button"
        className={`${ui.button} ${ui.large} ${ui.primary}`}
        onClick={check}
        disabled={!complete || saving || result === "right"}
      >
        <CheckIcon size={24} />
        {saving ? "Saving…" : "Done"}
      </button>
      <Feedback kind={result} />
    </>
  );
};

export default MatchingStep;
